import type { MRT_ColumnDef } from "mantine-react-table-open";
import {
  MantineReactTable,
  useMantineReactTable
} from "mantine-react-table-open";
import { useMemo } from "react";
import { getMinimalMRTOptions } from "../../../mantine/getMinimalMRTOptions";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import { sortBackendDates } from "../../../utils/dates/sortBackendDates";

export interface Paragraph24ZusammenfassungData {
  label: string;
  installedCapacity: string;
  site: string;
  eeg_inbetriebnahmedatum: string;
}

export interface Paragraph24ZusammenfassungTableProps {
  data?: Array<Paragraph24ZusammenfassungData>;
}

function Paragraph24ZusammenfassungTable({
  data = []
}: Paragraph24ZusammenfassungTableProps) {
  const dataSorted = useMemo(
    () =>
      [...data].sort((a, b) =>
        sortBackendDates(b.eeg_inbetriebnahmedatum, a.eeg_inbetriebnahmedatum)
      ),
    [data]
  );

  const sumInstalledCapacity = useMemo(
    () =>
      data.reduce((sum, row) => sum + Number(row.installedCapacity || 0), 0),
    [data]
  );
  const columns = useMemo<Array<MRT_ColumnDef<Paragraph24ZusammenfassungData>>>(
    () => [
      {
        accessorKey: "label",
        header: "Erzeuger",
        footer: "Summe"
      },
      {
        accessorKey: "site",
        header: "Liegenschaft"
      },
      {
        accessorKey: "installedCapacity",
        header: "Installierte Leistung",
        accessorFn: (row) => `${row.installedCapacity} kW`,
        aggregationFn: "sum",
        footer: `${sumInstalledCapacity} kW`
      },
      {
        accessorKey: "eeg_inbetriebnahmedatum",
        header: "EEG-Inbetriebnahmedatum",
        mantineTableHeadCellProps: {
          style: { whiteSpace: "nowrap" }
        },
        accessorFn: (row) =>
          backendDateOrDateTimeToLuxonDateTime(
            row.eeg_inbetriebnahmedatum
          ).toLocaleString({
            year: "numeric",
            month: "2-digit",
            day: "2-digit"
          })
      }
    ],
    [sumInstalledCapacity]
  );

  const table = useMantineReactTable<Paragraph24ZusammenfassungData>({
    ...getMinimalMRTOptions<Paragraph24ZusammenfassungData>(),
    columns,
    data: dataSorted
  });

  return <MantineReactTable table={table} />;
}

export { Paragraph24ZusammenfassungTable };
